/**
 * THE mapping from authored table-column config to ``ColumnDef``.
 *
 * Same reason as ``chart-props.ts``: the dashboard and the Home board
 * render the SAME ``DataTable``, so the config→columns translation must
 * exist once. The board had its own copy and it was missing three
 * things, each of which is a data defect rather than a styling one:
 *
 *  * **``hidden`` was not filtered.** Reports declare search-helper
 *    columns (a real one: ``_seller_names``, a ``STRING_AGG`` of every
 *    seller for an ASIN) that exist so free-text search can match them
 *    and are never meant to be read. The dashboard drops them; the
 *    board rendered them — leaking an internal column and adding
 *    thousands of px of width to a tile already too narrow.
 *  * **``key`` had no fallback.** A column authored with ``column`` but
 *    no ``key`` produced a key nothing in the row matches, so the whole
 *    column rendered as "—": a column of silent blanks where data
 *    exists.
 *  * **``render`` modes were dropped**, so tone / trend / heat /
 *    status / progress / compare cells fell back to plain text — the
 *    semantic channel (including the colourblind-safe shapes) gone.
 *
 * ``crossFilterKeys`` is host state (the dashboard has a filter bar,
 * a board tile does not), so it is a parameter: pass ``undefined`` and
 * every column renders non-clickable, which is exactly right for a
 * surface with nothing to cross-filter.
 */

import type { ColumnDef, ColumnRender } from "./DataTable";
import type { DataFormat } from "./formatting";
import type { DisplayLabel } from "./i18n-resolve";
import type { StateSpec } from "./types";

/** Cell render modes the pipeline may author. Anything else falls back
 *  to the default renderer rather than throwing. */
export const KNOWN_COLUMN_RENDERS: ReadonlySet<string> = new Set([
  "heat",
  "sparkline",
  "status",
  "progress",
  "compare",
  "trend",
  "tone",
  // A short categorical value that reads as an entity, not a
  // measurement (a retailer name). The pill is the affordance the
  // reference report uses to say "this row IS this thing".
  "chip",
  // Mixed columns (DSA 1.19, retailer table): "67 / 96" + "70 %" in
  // one cell, a delta in percentage points, a mini stacked mix bar and
  // a "Filter" button that cross-filters the report to the row.
  "fraction",
  "delta_pp",
  "mini_stack",
  "action",
  // Availability matrix cell (DSA 1.20): "ok" · "oos:N" · "fallen:N" ·
  // "never" tokens → glyph + text, never colour alone.
  "state_cell",
]);

/** The authored shape (a subset of the engine's table column config). */
export interface AuthoredColumn {
  key?: string;
  column?: string;
  label: string;
  format?: string;
  hidden?: boolean;
  render?: string;
  filter_key?: string;
  sub_key?: string;
  sticky?: boolean;
  max_width?: number;
  sub_label?: DisplayLabel | null;
  denominator_key?: string | null;
  stack?: ReadonlyArray<{ key: string; label?: DisplayLabel | null; color?: string | null }> | null;
  states?: Record<string, StateSpec> | null;
  badge_key?: string | null;
  cell_style?: string | null;
  thresholds?: ReadonlyArray<{ min: number | null; tone: "good" | "warn" | "bad" | "neutral" }> | null;
}

export function toColumnDefs(
  columns: readonly AuthoredColumn[],
  crossFilterKeys?: ReadonlySet<string>,
): ColumnDef[] {
  return columns
    .filter((c) => !c.hidden)
    .map((c) => ({
      // A column authored with `column` but no `key` still has to reach
      // its data — never a key that matches nothing.
      key: c.key || c.column?.replace(/^"|"$/g, "") || c.label,
      label: c.label,
      format: (c.format ?? "text") as DataFormat,
      sortable: true,
      render: KNOWN_COLUMN_RENDERS.has(c.render ?? "")
        ? (c.render as ColumnRender)
        : undefined,
      // Only honour filter_key when it names an eligible filter of the
      // HOST; a stale or unhosted target renders as a plain cell.
      filterKey:
        c.filter_key && crossFilterKeys?.has(c.filter_key)
          ? c.filter_key
          : undefined,
      // The subtext key travels verbatim: it names a key in the ROW,
      // not a column of this list (the column it names is normally
      // `hidden`, so it is filtered out above and would be unresolvable
      // against this array by construction).
      subKey: c.sub_key || undefined,
      // Congela la columna al scrollear horizontal. Solo cuenta como
      // prefijo: `DataTable` corta en la primera que no lo pide, porque
      // una columna fija en el medio dejaria un hueco.
      sticky: c.sticky === true,
      // Ancho maximo en px. A diferencia del `left` de sticky, este SI
      // lo declara el autor: el ancho de una columna de identidad es
      // una decision de diseno, no una medicion del contenido — y sin
      // techo un nombre largo se come la franja congelada entera.
      maxWidth:
        typeof c.max_width === "number" && c.max_width > 0
          ? c.max_width
          : undefined,
      // Mixed columns: the formula line, the denominator key and the
      // stack parts travel verbatim (keys name the ROW, like sub_key).
      subLabel: c.sub_label ?? undefined,
      denominatorKey: c.denominator_key || undefined,
      stack:
        c.stack && c.stack.length > 0
          ? c.stack.map((part) => ({
              key: part.key,
              label: part.label ?? undefined,
              color: part.color || undefined,
            }))
          : undefined,
      // The state map travels verbatim: it is the whole vocabulary of a
      // `state_cell` column (tokens, tones, glyphs, labels).
      states: c.states && Object.keys(c.states).length > 0 ? c.states : undefined,
      // Names a key of the ROW (usually a hidden column), like sub_key.
      badgeKey: c.badge_key || undefined,
      cellStyle: c.cell_style === "fill" ? "fill" : undefined,
      // `render: "tone"` bands. Without them the cell prints the config
      // error "⚠ sin bandas" — which every tone column of the DSA tables
      // (retailers' coverage / availability / presence, the matrix's days
      // down) showed, because this mapping never carried them.
      thresholds:
        c.thresholds && c.thresholds.length > 0 ? [...c.thresholds] : undefined,
    }));
}
